import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchJobs, fetchProfile, fetchCredits, startCaptureJob, type MixSettings } from '../lib/api.js'
import { langFlag, sortedLanguages } from '../lib/languageFlags.js'
import { useCapture } from '../hooks/useCapture.js'
import CaptureModal from '../components/CaptureModal.js'
import DubCard from '../components/DubCard.js'
import Skeleton from '../components/Skeleton.js'

// ── Helpers ───────────────────────────────────────────────────────────────────

function extractVideoId(url: string): string | null {
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([^&?/\s]{11})/,
  )
  return m?.[1] ?? null
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

interface PrefillState {
  url: string
  sourceLang: string
  targetLang: string
  originalVol: number
  dubbedVol: number
}

export default function DashboardPage() {
  const { t }  = useTranslation()
  const qc     = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()

  const isMobile = !('getDisplayMedia' in (navigator.mediaDevices ?? {}))
  const { activeCapture, captureQueue, startCapture, sharedStream, setSharedStream } = useCapture()

  // Check for prefill from NewDubDrawer
  const prefill = (location.state as { prefill?: PrefillState } | null)?.prefill

  // Form state
  const [url,            setUrl]            = useState(prefill?.url ?? '')
  const [lang,           setLang]           = useState(prefill?.targetLang ?? 'ko')
  const [sourceLang,     setSourceLang]     = useState(prefill?.sourceLang ?? 'en')
  const [langSeeded,     setLangSeeded]     = useState(!!prefill)
  const [urlError,       setUrlError]       = useState('')
  const [errorMsg,       setErrorMsg]       = useState('')
  const [originalVolume, setOriginalVolume] = useState(prefill?.originalVol ?? 15)
  const [dubbedVolume,   setDubbedVolume]   = useState(prefill?.dubbedVol ?? 100)

  // Highlight target lang selector on re-dub prefill
  const [highlightTarget, setHighlightTarget] = useState(false)

  // Sync prefill into form state (covers same-route navigation where useState initializers don't re-run)
  useEffect(() => {
    if (!prefill) return
    setUrl(prefill.url)
    setSourceLang(prefill.sourceLang)
    setLang(prefill.targetLang)
    setLangSeeded(true)
    setOriginalVolume(prefill.originalVol)
    setDubbedVolume(prefill.dubbedVol)
    setUrlError('')
    setErrorMsg('')
    setHighlightTarget(true)
    navigate(location.pathname, { replace: true, state: null })
  }, [prefill]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-dismiss highlight after animation
  useEffect(() => {
    if (!highlightTarget) return
    const t = setTimeout(() => setHighlightTarget(false), 2000)
    return () => clearTimeout(t)
  }, [highlightTarget])

  // Upload state used by mobile CaptureModal
  const [uploading]      = useState(false)

  // Job / capture state
  const [pendingInfo,      setPendingInfo]      = useState<{ jobId: string; videoUrl: string; targetLang: string } | null>(null)
  const [showMobileUpload, setShowMobileUpload] = useState(false)

  const langs = useMemo(() => sortedLanguages(t), [t])

  const { data: profile } = useQuery({ queryKey: ['profile'], queryFn: fetchProfile, staleTime: Infinity })
  const { data: credits } = useQuery({ queryKey: ['credits'], queryFn: fetchCredits, staleTime: 30_000 })

  useEffect(() => {
    if (profile?.preferredLang && !langSeeded) {
      setLang(profile.preferredLang)
      setLangSeeded(true)
    }
  }, [profile, langSeeded])

  const { data, isLoading } = useQuery({
    queryKey: ['jobs'],
    queryFn:  () => fetchJobs(20),
    refetchInterval: (query) => {
      const jobs     = query.state.data?.jobs ?? []
      const hasActive = jobs.some(j => !['ready', 'failed', 'expired'].includes(j.status))
      return (hasActive || pendingInfo || activeCapture) ? 3000 : false
    },
  })

  useEffect(() => {
    if (pendingInfo && data?.jobs.some(j => j.id === pendingInfo.jobId)) {
      setPendingInfo(null)
    }
  }, [data?.jobs, pendingInfo])

  useEffect(() => {
    if (!activeCapture && !uploading) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault() }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [activeCapture, uploading])

  const availableMinutes = credits
    ? Math.max(0, credits.studioMinutesTotal - credits.studioMinutesUsed - credits.studioMinutesReserved)
      + Math.max(0, credits.topupMinutesTotal - credits.topupMinutesUsed)
      + Math.max(0, 5 - credits.studioTrialMinutesUsed)
    : null
  const noCredits = availableMinutes !== null && availableMinutes <= 0

  const mixSettings: MixSettings = { duckPercent: 100 - originalVolume, originalVolume, dubbedVolume }

  // ── Queue banner ──────────────────────────────────────────────────────────
  const activeJobCount = (data?.jobs ?? []).filter(j => !['ready', 'failed', 'expired', 'queued'].includes(j.status)).length
  const queuedJobCount = (data?.jobs ?? []).filter(j => j.status === 'queued').length
  const showQueueBanner = activeJobCount > 0 || queuedJobCount > 0

  // ── Start handler ─────────────────────────────────────────────────────────

  const handleStart = async () => {
    setUrlError('')
    setErrorMsg('')

    const videoId = extractVideoId(url)
    if (!videoId) {
      setUrlError(t('capture.invalidUrl'))
      return
    }

    if (isMobile) {
      setShowMobileUpload(true)
      return
    }

    // Reuse existing stream if alive, otherwise request a new one.
    let audioStream: MediaStream
    const hasLiveStream = sharedStream && sharedStream.getAudioTracks().some(t => t.readyState === 'live')
    if (hasLiveStream) {
      audioStream = sharedStream!
    } else {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          // @ts-expect-error — Chrome-only: mute local playback while capturing
          audio: { suppressLocalAudioPlayback: true },
          preferCurrentTab: true,
          video: true,
        })
        stream.getVideoTracks().forEach(track => track.stop())

        const audioTracks = stream.getAudioTracks()
        if (audioTracks.length === 0) {
          stream.getTracks().forEach(t => t.stop())
          setUrlError(t('capture.noAudioTracks'))
          return
        }
        audioStream = new MediaStream(audioTracks)
        setSharedStream(audioStream)
      } catch (err) {
        const name = (err as Error).name
        if (name === 'NotAllowedError' || name === 'AbortError') {
          setUrlError(t('capture.errorDenied'))
        } else {
          setErrorMsg((err as Error).message)
        }
        return
      }
    }

    let videoTitle = ''
    const videoThumbnailUrl = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
    try {
      const oembed = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`)
      if (oembed.ok) {
        const data = await oembed.json() as { title?: string }
        videoTitle = data.title ?? ''
      }
    } catch { /* non-critical */ }

    try {
      const job = await startCaptureJob({
        targetLang: lang,
        captureRate: 2,
        videoUrl: url,
        videoTitle,
        videoThumbnailUrl,
        sourceLang,
        mixSettings,
      })

      startCapture({
        jobId:      job.id,
        videoId,
        videoUrl:   url,
        targetLang: lang,
        stream:     audioStream,
        uploadUrl:  job.uploadUrl,
        mixSettings,
      })
      setPendingInfo({ jobId: job.id, videoUrl: url, targetLang: lang })
      setUrl('')
      void qc.invalidateQueries({ queryKey: ['jobs'] })
    } catch (err) {
      if (captureQueue.length === 0) {
        audioStream.getTracks().forEach(t => t.stop())
      }
      setErrorMsg((err as Error).message)
    }
  }

  const handleMobileJobCreated = (jobId: string, videoUrl: string, targetLang: string) => {
    setPendingInfo({ jobId, videoUrl, targetLang })
    void qc.invalidateQueries({ queryKey: ['jobs'] })
    setShowMobileUpload(false)
    setUrl('')
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const totalJobs = data?.jobs?.length ?? 0

  return (
    <div>
      {/* ── Input card ────────────────────────────────────────────── */}
      <div className="dic">
        <div className="dic-title">{t('dashboard.watchTitle', 'What do you want to watch?')}</div>
        <div className="dic-sub">{t('dashboard.watchSub', 'Paste any YouTube URL — we\'ll dub it in your language with AI voice cloning.')}</div>

        {showQueueBanner && (
          <div className="queue-banner">
            <svg viewBox="0 0 24 24"><path d="M3 6h18M3 12h14M3 18h10" /></svg>
            <span>
              {activeJobCount > 0 && t('dashboard.dubRunning', { count: activeJobCount })}
              {activeJobCount > 0 && queuedJobCount > 0 && ', '}
              {queuedJobCount > 0 && t('dashboard.jobsQueued', { count: queuedJobCount })}
              . {t('dashboard.queueHint', 'You can add more — they start automatically.')}
            </span>
          </div>
        )}

        <div className="url-row">
          <div className="url-field">
            <svg viewBox="0 0 24 24"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" /></svg>
            <input
              type="text"
              placeholder={t('capture.urlPlaceholder')}
              value={url}
              onChange={e => { setUrl(e.target.value); setUrlError(''); setErrorMsg('') }}
              onKeyDown={e => { if (e.key === 'Enter') void handleStart() }}
            />
          </div>
          <button
            className="btn-dub"
            disabled={noCredits}
            onClick={() => void handleStart()}
          >
            {t('dashboard.dubThis', 'Dub This →')}
          </button>
          <button
            className="btn-upload"
            disabled={noCredits}
            onClick={() => setShowMobileUpload(true)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
            {t('dashboard.upload', 'Upload')}
          </button>
        </div>

        {urlError && <p style={{ color: 'var(--color-error, #ef4444)', fontSize: 13, marginTop: -8, marginBottom: 12 }}>{urlError}</p>}
        {errorMsg && <p style={{ color: 'var(--color-error, #ef4444)', fontSize: 13, marginTop: -8, marginBottom: 12 }}>{errorMsg}</p>}

        {/* Config row */}
        <div className="config-row">
          <div className="config-group">
            <div className="config-label">{t('capture.sourceLangLabel', 'Source')}</div>
            <select className="config-select" value={sourceLang} onChange={e => setSourceLang(e.target.value)}>
              {langs.map(l => (
                <option key={l} value={l}>{langFlag(l)} {t(`languages.${l}`)}</option>
              ))}
            </select>
          </div>
          <div className={`config-group${highlightTarget ? ' config-highlight' : ''}`}>
            <div className="config-label">{t('capture.langLabel', 'Target')}</div>
            <select className="config-select" value={lang} onChange={e => { setLang(e.target.value); setHighlightTarget(false) }}>
              {langs.map(l => (
                <option key={l} value={l}>{langFlag(l)} {t(`languages.${l}`)}</option>
              ))}
            </select>
          </div>
          <div className="config-group">
            <div className="config-label">{t('dashboard.originalVol', 'Original vol.')}</div>
            <div className="vol-wrap">
              <svg viewBox="0 0 24 24"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M15.54 8.46a5 5 0 010 7.07" /></svg>
              <input type="range" className="vol-slider" min="0" max="100" value={originalVolume} style={{ '--val': `${originalVolume}%` } as React.CSSProperties} onChange={e => setOriginalVolume(Number(e.target.value))} />
              <span className="vol-val">{originalVolume}%</span>
            </div>
          </div>
          <div className="config-group">
            <div className="config-label">{t('dashboard.dubbedVol', 'Dubbed vol.')}</div>
            <div className="vol-wrap">
              <svg viewBox="0 0 24 24"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M15.54 8.46a5 5 0 010 7.07" /><path d="M19.07 4.93a10 10 0 010 14.14" /></svg>
              <input type="range" className="vol-slider" min="0" max="100" value={dubbedVolume} style={{ '--val': `${dubbedVolume}%` } as React.CSSProperties} onChange={e => setDubbedVolume(Number(e.target.value))} />
              <span className="vol-val">{dubbedVolume}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Dub cards grid ─────────────────────────────────────────── */}
      <div className="section-head">
        <span className="section-title">{t('dashboard.myDubs', 'My Dubs')}</span>
        <span className="section-count">{totalJobs} {t('dashboard.videos', 'videos')}</span>
      </div>

      {isLoading ? (
        <div className="dub-grid">
          {[1, 2, 3].map(i => (
            <div key={i} className="dub-card" style={{ pointerEvents: 'none' }}>
              <div className="dub-card-thumb"><div className="skeleton-shimmer" style={{ width: '100%', height: '100%' }} /></div>
              <div className="dub-card-info"><Skeleton rows={2} height="0.85rem" /></div>
            </div>
          ))}
        </div>
      ) : totalJobs === 0 && !pendingInfo ? (
        <div style={{ textAlign: 'center', padding: '48px 12px', fontSize: 13, color: 'var(--sub)' }}>
          <p style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-app)' }}>{t('dashboard.emptyTitle')}</p>
          <p>{t('dashboard.emptyDesc')}</p>
        </div>
      ) : (
        <div className="dub-grid">
          {data?.jobs.map(job => (
            <DubCard key={job.id} job={job} />
          ))}
        </div>
      )}

      {/* Mobile file upload modal */}
      {showMobileUpload && (
        <CaptureModal
          videoUrl={url}
          targetLang={lang}
          sourceLang={sourceLang}
          mixSettings={mixSettings}
          onClose={() => setShowMobileUpload(false)}
          onJobCreated={handleMobileJobCreated}
        />
      )}
    </div>
  )
}
